import { Entity, FieldName } from "./utils/CustomTypes"
import { TableFields } from "./utils/TableFields"

export type CustomEnvironment = {
  name: string,
  urlApp: string,
  urlWeb: string,
  urlWebName: string,
  urlPrivacy: string,
  urlTerms: string,
  urlStatus: string,
  urlSLA: string,
  logo: string,
  logoWidth: number,
  entities: {
    contact: {
      type: Entity,
      defaultQueryString: string,
      queryEditorSupportedFields: string[],
      urlSegment: string,
      tableFields: TableFields,
    },
    institution: {
      type: Entity,
      name: string,
      namePlural: string,
      urlSegment: string,
      defaultQueryString: string,
      tableFields: TableFields,
      queryEditorSupportedFields: string[],
    },
    domains: {
      type: Entity,
      name: string,
      namePlural: string,
      urlSegment: string,
      defaultQueryString: string,
      tableFields: TableFields,
      queryEditorSupportedFields: string[],
    },
    ingestions: {
      type: Entity,
      name: string,
      namePlural: string,
      urlSegment: string,
      defaultQueryString: string,
      tableFields: TableFields,
      queryEditorSupportedFields: string[],
    },
    rfps: {
      type: Entity,
      name: string,
      namePlural: string,
      urlSegment: string,
      defaultQueryString: string,
      tableFields: TableFields,
      queryEditorSupportedFields: string[],
    },
  },
  flags: {
    shouldEnableServiceAgenciesPage: boolean,
    shouldEnableTrackingPage: boolean,
    shouldEnableProspectorPage: boolean,
    shouldEnableNewHiresPage: boolean,
    shouldEnableDomainsPage: boolean,
  },
}

export function getFlavor(): string {
  try {
    return import.meta.env.VITE_FLAVOR || 'EDULEAD'
  } catch {
    return 'EDULEAD'
  }
}

export function getEnvironment(): CustomEnvironment {
  const flavor = getFlavor()
  console.log('Getting environment')
  return environment?.[flavor] || environment['EDULEAD']
}

const environment: { [key: string]: CustomEnvironment } = {
  EDULEAD: {
    name: 'Edulead',
    urlApp: 'https://app.getedulead.com',
    urlWeb: 'https://www.getedulead.com',
    urlWebName: 'getedulead.com',
    urlPrivacy: 'https://getedulead.com/privacy',
    urlTerms: 'https://getedulead.com/terms',
    urlStatus: 'https://status.getedulead.com',
    urlSLA: 'https://getedulead.com/sla',
    logo: 'logo-edulead.svg',
    logoWidth: 107,
    entities: {
      contact: {
        type: Entity.CONTACT,
        urlSegment: 'contacts',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJ0aXRsZVJvbGUiLCJvcGVyYXRvciI6ImVxdWFscyIsInZhbHVlIjoiZGlyZWN0b3IifSx7ImZpZWxkIjoidGl0bGVBcmVhIiwib3BlcmF0b3IiOiJlcXVhbHMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJjdXJyaWN1bHVtIGFuZCBpbnN0cnVjdGlvbix0ZWNobm9sb2d5In1dLCJjb21iaW5hdG9yIjoiYW5kIiwibm90IjpmYWxzZX0=&page=1&sortBy=relevance&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.CONTACT,
          options: [{
            fieldName: FieldName.NAME,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: 1.8 },
          }, {
            fieldName: FieldName.TITLE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
          }, {
            fieldName: FieldName.BIO,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
          }, {
            fieldName: FieldName.EMAIL,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { flex: 1 },
          }, {
            fieldName: FieldName.PHONE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
          }]
        }),
        // EDU-53: emailVerificationStatus, emailSource, labels, grade and fulltext are no longer
        // offered in the picker (QueryEditor CustomSourceSelector) but stay listed here so rules
        // in older saved searches still render with their proper editors and can be edited/removed.
        queryEditorSupportedFields: [
          'zip',
          'ncesId',
          'districtRevTotal',
          'studentsTotal',
          'stfTotal',
          'countyId',
          'fulltext',
          'grade',
          'schoolGrade',
          'name',
          'title',
          'titleModifier',
          'titleRole',
          'titleArea',
          'role.marketThemes',
          'role.decisionLevel',
          'role.functionalGroup',
          'id',
          'email',
          'emailType',
          'emailVerificationStatus',
          'emailSource',
          'phone',
          'phoneUniqueness',
          'shapes',
          'radiusSearch',
          'state',
          'territoryId',
          'institutionType',
          'subtype',
          'model',
          'religiousAffiliation',
          'titleOne',
          'institutionName',
          'institutionId',
          'access',
          'labels',
        ]
      },
      institution: {
        type: Entity.DISTRICT,
        name: 'Institution',
        namePlural: 'Institutions',
        urlSegment: 'institutions',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJ0eXBlIiwib3BlcmF0b3IiOiJlcXVhbHMiLCJ2YWx1ZSI6IkRJU1RSSUNUIn0seyJmaWVsZCI6InN1YnR5cGUiLCJvcGVyYXRvciI6ImVxdWFscyIsInZhbHVlIjoiUkVHVUxBUiJ9LHsiZmllbGQiOiJzdGF0ZSIsIm9wZXJhdG9yIjoiZXF1YWxzIiwidmFsdWUiOiJUWCxDQSJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=name&sortDir=asc',
        tableFields: new TableFields({
          disambiguator: Entity.DISTRICT,
          options: [{
            fieldName: FieldName.NAME,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: 1.8 },
          }, {
            fieldName: FieldName.TYPE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { width: '90px' },
          }, {
            fieldName: FieldName.SUBTYPE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { width: '90px' },
          }, {
            fieldName: FieldName.ACCESS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { width: '90px' },
          }, {
            fieldName: FieldName.STUDENTS_TOTAL,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { width: '110px' },
          }, {
            fieldName: FieldName.TOTAL_SCHOOLS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { width: '100px' },
          }]
        }),
        queryEditorSupportedFields: [
          'type',
          'subtype',
          'access',
          'name',
          'fulltext',
          'phone',
          'fax',
          'studentsTotal',
          'stfTotal',
          'titleOne',
          'model',
          'religiousAffiliation',
          'state',
          'zip',
          'countyId',
          'territoryId',
          'shapes',
          'radiusSearch',
          'ncesId',
          'idParentInstitution',
          'grade',
        ]
      },
      domains: {
        type: Entity.DOMAIN,
        name: 'Domain',
        namePlural: 'Domains',
        urlSegment: 'domains',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJpZCIsIm9wZXJhdG9yIjoiY29udGFpbnMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJrMTIifSx7ImZpZWxkIjoibnJJbXBvcnRhbnRVcmxzIiwib3BlcmF0b3IiOiI%2BIiwidmFsdWVTb3VyY2UiOiJ2YWx1ZSIsInZhbHVlIjoiMCJ9LHsiZmllbGQiOiJuckluc3RpdHV0aW9ucyIsIm9wZXJhdG9yIjoiPiIsInZhbHVlU291cmNlIjoidmFsdWUiLCJ2YWx1ZSI6IjAifSx7ImZpZWxkIjoibnJMaW5rZWREb21haW5zIiwib3BlcmF0b3IiOiI%2BIiwidmFsdWVTb3VyY2UiOiJ2YWx1ZSIsInZhbHVlIjoiMCJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=id&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.DOMAIN,
          options: [{
            fieldName: FieldName.ID,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: 2.8 },
          }, {
            fieldName: FieldName.NR_INSTITUTIONS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { flex: 1 },
          }, {
            fieldName: FieldName.NR_IMPORTANT_URLS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { flex: 1 },
          }, {
            fieldName: FieldName.NR_LINKED_DOMAINS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { flex: 1 },
          }]
        }),
        queryEditorSupportedFields: [
          'id',
          'nrInstitutions',
          'nrImportantUrls',
          'nrLinkedDomains',
        ]
      },
      ingestions: {
        type: Entity.INGESTION,
        name: 'Tracking',
        namePlural: 'Tracking',
        urlSegment: 'tracking',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJpZCIsIm9wZXJhdG9yIjoiY29udGFpbnMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJrMTIifSx7ImZpZWxkIjoibnJJbXBvcnRhbnRVcmxzIiwib3BlcmF0b3IiOiI%2BIiwidmFsdWVTb3VyY2UiOiJ2YWx1ZSIsInZhbHVlIjoiMCJ9LHsiZmllbGQiOiJuckluc3RpdHV0aW9ucyIsIm9wZXJhdG9yIjoiPiIsInZhbHVlU291cmNlIjoidmFsdWUiLCJ2YWx1ZSI6IjAifSx7ImZpZWxkIjoibnJMaW5rZWREb21haW5zIiwib3BlcmF0b3IiOiI%2BIiwidmFsdWVTb3VyY2UiOiJ2YWx1ZSIsInZhbHVlIjoiMCJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=id&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.INGESTION,
          options: [{
            fieldName: FieldName.INGESTED_AT,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: false,
            style: { flex: 0.4 },
          }, {
            fieldName: FieldName.NEARBY_INSTITUTIONS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { flex: 0.4 },
          }]
        }),
        queryEditorSupportedFields: []
      },
      rfps: {
        type: Entity.RFP,
        name: 'Contract',
        namePlural: 'Contracts',
        urlSegment: 'contracts',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJjb250cmFjdF9kdXJhdGlvbiIsIm9wZXJhdG9yIjoiYmV0d2VlbiIsInZhbHVlU291cmNlIjoidmFsdWUiLCJ2YWx1ZSI6IjMsNCJ9LHsiZmllbGQiOiJkZXNjcmlwdGlvbiIsIm9wZXJhdG9yIjoiY29udGFpbnMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJzb2Z0d2FyZSJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=date_contract_end&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.RFP,
          options: [{
            fieldName: FieldName.VENDOR_NAME,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: '150px' },
          }, {
            fieldName: FieldName.DATE_CONTRACT_EFFECTIVE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { width: '100px' },
          }, {
            fieldName: FieldName.DATE_CONTRACT_END,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { width: '100px' },
          }, {
            fieldName: FieldName.DESCRIPTION,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            // style: { flex: 0.4 },
          }, {
            fieldName: FieldName.CONTRACT_DURATION,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { width: '130px' },
          }, {
            fieldName: FieldName.INSTITUTION_NAME,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            // style: { width: '130px' },
          }]
        }),
        queryEditorSupportedFields: [
          'vendor_name',
          'description',
          'contract_duration',
        ]
      },
    },
    flags: {
      shouldEnableServiceAgenciesPage: true,
      shouldEnableTrackingPage: true,
      shouldEnableProspectorPage: true,
      shouldEnableNewHiresPage: true,
      shouldEnableDomainsPage: true,
    },
  },
  HEALTH: {
    name: 'ClearNode',
    urlApp: 'https://app.clearnode.io',
    urlWeb: 'https://www.clearnode.io',
    urlWebName: 'clearnode.io',
    urlPrivacy: 'https://clearnode.io/privacy',
    urlTerms: 'https://clearnode.io/terms',
    urlStatus: 'https://status.clearnode.io',
    urlSLA: 'https://clearnode.io/sla',
    logo: 'logo-clearnode.svg',
    logoWidth: 142,
    entities: {
      contact: {
        type: Entity.CONTACT,
        urlSegment: 'contacts',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJ0aXRsZSIsIm9wZXJhdG9yIjoiPSIsInZhbHVlIjoiQ2hpZWYgRmluYW5jaWFsIE9mZmljZXIsIFByZXNpZGVudCJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=relevance&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.CONTACT,
          options: [{
            fieldName: FieldName.NAME,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: 1.8 },
          }, {
            fieldName: FieldName.TITLE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
          }, {
            fieldName: FieldName.BIO,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
          }, {
            fieldName: FieldName.EMAIL,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { flex: 1 },
          }, {
            fieldName: FieldName.PHONE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
          }]
        }),
        queryEditorSupportedFields: [
          'zip',
          'countyId',
          'fulltext',
          'name',
          'title',
          'titleModifier',
          'titleRole',
          'titleArea',
          'email',
          'emailType',
          'emailVerificationStatus',
          'emailSource',
          'phone',
          'phoneUniqueness',
          'shapes',
          'radiusSearch',
          'state',
          'institutionType',
          'subtype',
          'model',
          'access',
          'labels'
        ]
      },
      institution: {
        type: Entity.DISTRICT,
        name: 'Institution',
        namePlural: 'Institutions',
        urlSegment: 'institutions',
        defaultQueryString: '?q=eyJjb21iaW5hdG9yIjoiYW5kIiwicnVsZXMiOltdfQ==&page=1&sortBy=name&sortDir=asc',
        tableFields: new TableFields({
          disambiguator: Entity.DISTRICT,
          options: [{
            fieldName: FieldName.NAME,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: 1 }
          }]
        }),
        queryEditorSupportedFields: [
          'type',
          'name',
          'state',
          'radiusSearch',
          'idParentInstitution',
        ]
      },
      domains: {
        type: Entity.DOMAIN,
        name: 'Domain',
        namePlural: 'Domains',
        urlSegment: 'domains',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJpZCIsIm9wZXJhdG9yIjoiY29udGFpbnMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJrMTIifV0sImNvbWJpbmF0b3IiOiJhbmQiLCJub3QiOmZhbHNlfQ==&page=1&sortBy=id&sortDir=asc',
        tableFields: new TableFields({
          disambiguator: Entity.DOMAIN,
          options: [{
            fieldName: FieldName.ID,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: 1.3 },
          }]
        }),
        queryEditorSupportedFields: [
          'id'
        ]
      },
      ingestions: {
        type: Entity.INGESTION,
        name: 'Tracking',
        namePlural: 'Tracking',
        urlSegment: 'tracking',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJpZCIsIm9wZXJhdG9yIjoiY29udGFpbnMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJrMTIifSx7ImZpZWxkIjoibnJJbXBvcnRhbnRVcmxzIiwib3BlcmF0b3IiOiI%2BIiwidmFsdWVTb3VyY2UiOiJ2YWx1ZSIsInZhbHVlIjoiMCJ9LHsiZmllbGQiOiJuckluc3RpdHV0aW9ucyIsIm9wZXJhdG9yIjoiPiIsInZhbHVlU291cmNlIjoidmFsdWUiLCJ2YWx1ZSI6IjAifSx7ImZpZWxkIjoibnJMaW5rZWREb21haW5zIiwib3BlcmF0b3IiOiI%2BIiwidmFsdWVTb3VyY2UiOiJ2YWx1ZSIsInZhbHVlIjoiMCJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=id&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.INGESTION,
          options: [{
            fieldName: FieldName.INGESTED_AT,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: false,
            style: { flex: 0.4 },
          }, {
            fieldName: FieldName.NEARBY_INSTITUTIONS,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            style: { flex: 0.4 },
          }]
        }),
        queryEditorSupportedFields: []
      },
      rfps: {
        type: Entity.RFP,
        name: 'Contract',
        namePlural: 'Contracts',
        urlSegment: 'contracts',
        defaultQueryString: '?q=eyJydWxlcyI6W3siZmllbGQiOiJjb250cmFjdF9kdXJhdGlvbiIsIm9wZXJhdG9yIjoiYmV0d2VlbiIsInZhbHVlU291cmNlIjoidmFsdWUiLCJ2YWx1ZSI6IjMsNCJ9LHsiZmllbGQiOiJkZXNjcmlwdGlvbiIsIm9wZXJhdG9yIjoiY29udGFpbnMiLCJ2YWx1ZVNvdXJjZSI6InZhbHVlIiwidmFsdWUiOiJzb2Z0d2FyZSJ9XSwiY29tYmluYXRvciI6ImFuZCIsIm5vdCI6ZmFsc2V9&page=1&sortBy=date_contract_end&sortDir=desc',
        tableFields: new TableFields({
          disambiguator: Entity.RFP,
          options: [{
            fieldName: FieldName.VENDOR_NAME,
            isVisibleByDefault: true,
            isHideable: false,
            isSortable: true,
            style: { flex: '150px' },
          }, {
            fieldName: FieldName.DATE_CONTRACT_EFFECTIVE,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { width: '100px' },
          }, {
            fieldName: FieldName.DATE_CONTRACT_END,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { width: '100px' },
          }, {
            fieldName: FieldName.DESCRIPTION,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            // style: { flex: 0.4 },
          }, {
            fieldName: FieldName.CONTRACT_DURATION,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: true,
            style: { width: '130px' },
          }, {
            fieldName: FieldName.INSTITUTION_NAME,
            isVisibleByDefault: true,
            isHideable: true,
            isSortable: false,
            // style: { width: '130px' },
          }]
        }),
        queryEditorSupportedFields: [
          'vendor_name',
          'description',
          'contract_duration',
        ]
      },
    },
    flags: {
      shouldEnableServiceAgenciesPage: false,
      shouldEnableTrackingPage: false,
      shouldEnableProspectorPage: false,
      shouldEnableNewHiresPage: false,
      shouldEnableDomainsPage: true,
    },
  }
}
